import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

const nearbyAreas = [
  { href: "/water-tank-cleaning-dlf-phase-gurugram", label: "DLF Phase 1-5" },
  { href: "/water-tank-cleaning-sushant-lok-gurugram", label: "Sushant Lok" },
  { href: "/water-tank-cleaning-palam-vihar-gurugram", label: "Palam Vihar" },
  { href: "/water-tank-cleaning-golf-course-road-gurugram", label: "Golf Course Road" },
];

export default function Blog5() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>DIY vs Professional Tank Cleaning in Gurugram - Cost Comparison | TankBuddy</title>
        <meta name="description" content="Is DIY water tank cleaning worth it in Gurugram? Compare costs, risks and results vs professional cleaning. TankBuddy +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/blog/diy-vs-professional-tank-cleaning-gurugram" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            TankBuddy Blog
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            DIY vs Professional Tank Cleaning - Cost Comparison
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">
              Many Gurugram homeowners consider cleaning their water tank themselves to save money. But is DIY tank cleaning actually cheaper - and more importantly, is it effective? We break down the real costs and risks of both options.
            </p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">
              The short answer: DIY cleaning costs more in the long run and leaves your tank 60-70% cleaner than professional cleaning. Here is why.
            </p>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Professional Cleaning Today</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              The Real Cost of DIY Tank Cleaning
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Most people underestimate what DIY cleaning actually involves. Here is what you need:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                {title:"Cleaning Equipment", desc:"Brushes, scrubbers, pressure washer rental - Rs. 500-1,500"},
                {title:"Chemicals", desc:"Chlorine tablets, anti-bacterial solution - Rs. 300-600"},
                {title:"Your Time", desc:"4-6 hours minimum for a proper clean - priceless"},
                {title:"Risk of Injury", desc:"Rooftop access and confined spaces carry real dangers"},
              ].map((item) => (
                <div key={item.title} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <h3 className="font-heading font-bold text-navy mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-600 leading-relaxed">
              Total DIY cost: <strong>Rs. 800-2,100+</strong> - plus your time, physical effort and safety risk. And without professional equipment, you will miss bacteria in corners, biofilm on walls and sediment in hard-to-reach areas.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Professional Cleaning - What You Get
            </h2>
            <ul className="space-y-3 mb-4">
              {[
                "Certified technicians with proper safety equipment",
                "Industrial high-pressure jet cleaning equipment",
                "Food-grade anti-bacterial and UV disinfection treatment",
                "Complete sludge and sediment removal",
                "30-day hygiene guarantee included",
                "No effort or risk on your part - we handle everything"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-sky/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sky text-xs font-bold">checkmark</span>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed">
              TankBuddy professional cleaning: <strong>Starting Rs. 999</strong> - cheaper than most DIY attempts, with guaranteed results and zero effort from you.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Side by Side Comparison
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {title:"DIY Cleaning", points:["Cost: Rs. 800-2,100+","Time: 4-6 hours","Risk: High (rooftop, chemicals)","Result: 60-70% clean","No guarantee"]},
                {title:"TankBuddy Professional", points:["Cost: Starting Rs. 999","Time: You do nothing","Risk: Zero for homeowner","Result: 100% bacteria free","30-day guarantee included"]},
              ].map((col) => (
                <div key={col.title} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <h3 className="font-heading font-bold text-navy mb-3">{col.title}</h3>
                  <ul className="space-y-2">
                    {col.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                        <span className="text-sky mt-0.5">•</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              The Verdict
            </h2>
            <p className="text-slate-600 leading-relaxed">
              For most Gurugram homeowners, professional cleaning is both cheaper and more effective than DIY. When you factor in your time, equipment costs, safety risk and the fact that DIY cleaning leaves bacteria behind - TankBuddy starting price of Rs. 999 is an easy decision. Book today and get it done right.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in Gurugram</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map(a => (
                <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                  <MapPin className="w-3 h-3 text-sky" />{a.label}
                </a>
              ))}
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
